import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import {
  deriveOrganizationContext,
  AppError,
  type OrganizationContext,
} from 'ozira-app-core';
import { supabase } from '../lib/supabaseClient';
import { apiClient } from '../lib/apiClient';

type SessionStatus = 'idle' | 'loading' | 'ready' | 'error';

interface SessionState {
  status: SessionStatus;
  email: string | null;
  organization: OrganizationContext;
  error: AppError | null;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (name: string, email: string, password: string) => Promise<{ needsEmailConfirmation: boolean }>;
  signInWithProvider: (provider: 'google' | 'apple') => Promise<void>;
  signOut: () => Promise<void>;
  refreshPrincipal: () => Promise<void>;
}

type PrincipalLoadResult = 'verified' | 'unauthenticated' | 'unavailable';

const SessionContext = createContext<SessionState | null>(null);

export function useSession(): SessionState {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession must be used within SessionProvider');
  return ctx;
}

export function SessionProvider({ children }: { children: React.ReactNode }) {
  const [status, setStatus] = useState<SessionStatus>('idle');
  const [email, setEmail] = useState<string | null>(null);
  const [organization, setOrganization] = useState<OrganizationContext>(
    deriveOrganizationContext({ sessionStatus: 'idle', principal: null }),
  );
  const [error, setError] = useState<AppError | null>(null);
  const directlyVerifiedAccessToken = useRef<string | null>(null);
  const sessionRevision = useRef(0);

  // The ONLY place a client-side Principal is minted: from the backend's
  // own verified response, never from anything stored locally (section 16).
  const loadPrincipal = useCallback(async (accessToken?: string, isCurrent: () => boolean = () => true): Promise<PrincipalLoadResult> => {
    try {
      const { data } = await apiClient.call('/api/me', accessToken ? { accessToken } : undefined);
      if (!isCurrent()) return 'unavailable';
      const user = (data as { user?: { id?: unknown } } | undefined)?.user;
      if (!user || typeof user.id !== 'string' || !user.id) {
        setError(new AppError('UNAUTHENTICATED', 'Sign in required.'));
        setOrganization(deriveOrganizationContext({ sessionStatus: 'error', principal: null }));
        return 'unauthenticated';
      }
      const principal = { schemaVersion: 1 as const, principalId: user.id, subjectType: 'USER' as const };
      setOrganization(deriveOrganizationContext({ sessionStatus: 'ready', principal }));
      setError(null);
      return 'verified';
    } catch (err) {
      if (!isCurrent()) return 'unavailable';
      const appError = err instanceof AppError ? err : new AppError('UNKNOWN', 'Could not load identity.');
      setError(appError);
      setOrganization(deriveOrganizationContext({ sessionStatus: 'error', principal: null }));
      return appError.code === 'UNAUTHENTICATED' ? 'unauthenticated' : 'unavailable';
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const settleSession = async (session: Awaited<ReturnType<typeof supabase.auth.getSession>>['data']['session'], currentRevision: number) => {
      if (cancelled || currentRevision !== sessionRevision.current) return;
      // signIn() verifies the exact token returned by Supabase. Supabase can
      // deliver the matching SIGNED_IN notification after that verification;
      // do not run a second identity check against a transiently incomplete
      // session and overwrite the verified state.
      if (session?.access_token && session.access_token === directlyVerifiedAccessToken.current) {
        directlyVerifiedAccessToken.current = null;
        setStatus('ready');
        return;
      }
      if (session) {
        // Auth events can be delivered while the SDK is still persisting or
        // refreshing its session. Use the token carried by this exact event;
        // reading the session again can briefly produce an unauthenticated API
        // request and log a valid user back out.
        const result = await loadPrincipal(session.access_token, () => !cancelled && currentRevision === sessionRevision.current);
        if (cancelled || currentRevision !== sessionRevision.current) return;
        setEmail(result === 'verified' ? session.user.email ?? null : null);
        if (result === 'unauthenticated') void supabase.auth.signOut({ scope: 'local' });
        setStatus(result === 'unavailable' ? 'error' : 'ready');
        return;
      } else {
        setEmail(null);
        setError(null);
        setOrganization(deriveOrganizationContext({ sessionStatus: 'ready', principal: null }));
      }
      setStatus('ready');
    };

    setStatus('loading');
    (async () => {
      const currentRevision = ++sessionRevision.current;
      try {
        const { data, error: sessionError } = await supabase.auth.getSession();
        if (sessionError) throw sessionError;
        await settleSession(data.session, currentRevision);
      } catch {
        if (cancelled || currentRevision !== sessionRevision.current) return;
        setError(new AppError('UNKNOWN', 'Could not restore your session.'));
        setStatus('error');
      }
    })();

    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      setStatus('loading');
      const currentRevision = ++sessionRevision.current;
      // Supabase documents a client deadlock when async Auth API work is
      // awaited inside this callback. Return synchronously, then verify the
      // backend Principal on the next task after the auth lock is released.
      window.setTimeout(() => {
        void settleSession(session, currentRevision);
      }, 0);
    });

    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, [loadPrincipal]);

  const signIn = useCallback(async (signInEmail: string, password: string) => {
    setStatus('loading');
    // Invalidate the initial getSession() settle that may still be in flight.
    // Without this, a pre-login anonymous result can land after a successful
    // sign-in and overwrite the verified organization context.
    ++sessionRevision.current;
    try {
      const { data, error: signInError } = await supabase.auth.signInWithPassword({ email: signInEmail, password });
      if (signInError) {
        setStatus('ready');
        throw new AppError('VALIDATION', signInError.message);
      }
      if (!data.session) {
        setStatus('ready');
        throw new AppError('UNAUTHENTICATED', 'Supabase did not establish a session.');
      }
      // Do not rely solely on onAuthStateChange. The runtime Supabase client
      // is resolved asynchronously, so a fast sign-in can complete before
      // that listener has been attached and the SIGNED_IN event is then missed.
      // Verify the backend Principal as part of the sign-in promise itself;
      // the listener remains responsible for restored/refreshed sessions.
      // Use the token returned by this exact sign-in. Reading session storage
      // again here can briefly return null while the SDK finishes persisting
      // the new session, causing /api/me to be sent without Authorization.
      directlyVerifiedAccessToken.current = data.session.access_token;
      const result = await loadPrincipal(data.session.access_token);
      if (result !== 'verified') {
        directlyVerifiedAccessToken.current = null;
        setEmail(null);
        setStatus('ready');
        await supabase.auth.signOut({ scope: 'local' });
        throw new AppError('UNAUTHENTICATED', 'OZIRA could not verify the signed-in session.');
      }
      setEmail(data.session.user.email ?? null);
      setStatus('ready');
    } catch (err) {
      directlyVerifiedAccessToken.current = null;
      throw err;
    }
  }, [loadPrincipal]);

  const signInWithProvider = useCallback(async (provider: 'google' | 'apple') => {
    const { error: providerError } = await supabase.auth.signInWithOAuth({
      provider,
      options: { redirectTo: `${window.location.origin}/` },
    });
    if (providerError) throw new AppError('VALIDATION', providerError.message);
  }, []);

  const signUp = useCallback(async (name: string, signUpEmail: string, password: string) => {
    setStatus('loading');
    ++sessionRevision.current;
    try {
      const { data, error: signUpError } = await supabase.auth.signUp({
        email: signUpEmail,
        password,
        options: { data: { full_name: name.trim() } },
      });
      if (signUpError) throw new AppError('VALIDATION', signUpError.message);
      if (!data.session) {
        setEmail(null);
        setOrganization(deriveOrganizationContext({ sessionStatus: 'ready', principal: null }));
        setStatus('ready');
        return { needsEmailConfirmation: true };
      }
      const result = await loadPrincipal(data.session.access_token);
      if (result !== 'verified') {
        await supabase.auth.signOut({ scope: 'local' });
        throw new AppError('UNAUTHENTICATED', 'OZIRA could not verify the newly created session.');
      }
      setEmail(data.session.user.email ?? null);
      setStatus('ready');
      return { needsEmailConfirmation: false };
    } catch (err) {
      setStatus('ready');
      throw err;
    }
  }, [loadPrincipal]);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
  }, []);

  const refreshPrincipal = useCallback(async () => {
    const result = await loadPrincipal();
    if (result !== 'verified') setEmail(null);
  }, [loadPrincipal]);

  const value: SessionState = { status, email, organization, error, signIn, signUp, signInWithProvider, signOut, refreshPrincipal };
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}
